import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { useSyncPatientBaselineFromReview } from "features/cdm/hooks/useSyncPatientBaselineFromReview";
import { toReviewLocalDate } from "features/cdm/utils/reviewDate";
import { toNumberOrUndefined } from "features/cdm/utils/toNumberOrUndefined";

export const useSendPP = ({ patientId }) => {
  const queryClient = useQueryClient();
  const { syncBaseline } = useSyncPatientBaselineFromReview({ patientId });

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    // Callers surface the real BE message via extractSubmissionError; suppress
    // the generic global mutation toast. 403 is still handled globally.
    meta: { skipGlobalError: true },
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.put(
        `${tenantVal}/cdm/patients/${patientId}/pp/submit`,
        {
          reviewType: data.reviewType,
          reviewDate: toReviewLocalDate(data.reviewDate),
          originalReimbursementMessageControlId:
            data?.reimbursementMessageControlId || null,
          // Reason for Correction (X0339-2/3/4) — only on a correction; the GP
          // picks one of three values; BE emits the X0339 OBX in the CDR message.
          correctionReason: data?.reimbursementMessageControlId
            ? data.correctionReason || null
            : null,
          clinicalData: {
            patientDemographics: {
              ethnicityCode: data.ethnicityCode,
            },
            registrationReasons: data.registrationReasons,
            yearOfRegistration: Number(data.yearOfRegistration),
            additionalRiskFactors: data.additionalRiskFactors ?? [],
            yearOfAdditionalRiskFactor: data.yearOfAdditionalRiskFactor ?? {},
            medicationReview: data.medicationReview === "yes" ? true : false,
            riskFactors: {
              smokingStatus: data.smokingStatus,
              vapingStatus: data.vapingStatus,
              weightKg: Number(data.weight),
              heightCm: Number(data.height),
              bmi: Number(data.bmi),
              waistCircumferenceCm: Number(data.waistCircumference),
              qrisk3Score: Number(data.qRisk3Score),
              alcoholAuditCScore: Number(data.alcoholRiskScore),
              alcoholFullAuditScore: data.alcoholFullAuditScore ? Number(data.alcoholFullAuditScore) : null,
              physicalActivityQ1: data.physicalActivityQ1,
              physicalActivityQ2: data.physicalActivityQ2,
              smokingInterventions: (data.smokingInterventions || []).filter(
                Boolean
              ),
              alcoholDependenceInterventions: (
                data.alcoholDependenceInterventions || []
              ).filter(Boolean),
              alcoholHigherRiskInterventions: (
                data.alcoholHigherRiskInterventions || []
              ).filter(Boolean),
              alcoholIncreasingRiskInterventions: (
                data.alcoholIncreasingRiskInterventions || []
              ).filter(Boolean),
              weightHighRiskInterventions: (
                data.weightHighRiskInterventions || []
              ).filter(Boolean),
              weightIncreasedRiskInterventions: (
                data.weightIncreasedRiskInterventions || []
              ).filter(Boolean),
              weightNormalInterventions: (
                data.weightNormalInterventions || []
              ).filter(Boolean),
              physicalActivityInterventions: (
                data.physicalActivityInterventions || []
              ).filter(Boolean),
              influenzaVaccine: data.influenzaVaccine || null,
              influenzaVaccineDate: data.influenzaVaccineDate
                ? data.influenzaVaccineDate.split("-").reverse().join("-")
                : null,
              pneumococcalVaccine: data.pneumococcalVaccine || null,
              pneumococcalVaccineDate: data.pneumococcalVaccineDate
                ? data.pneumococcalVaccineDate.split("-").reverse().join("-")
                : null,
              covidVaccine: data.covidVaccine || null,
              covidVaccineStatusUpToDate:
                data.covidVaccineStatusUpToDate === "YES",
            },
            physicalExam: {
              pulseRate: Number(data.pulse),
              pulseRhythm: data.pulseRhythm,
              systolicBloodPressure: Number(data.bp1),
              diastolicBloodPressure: Number(data.bp2),
            },
            diagnosticInvestigations: {
              ecgSinceLastReview:
                data.ecgSinceLastReview === "YES" ? true : false,
              ecgResult: data.ecgResult || null,
              lastEcgDate: data.lastEcgDate ? Number(data.lastEcgDate) : null,
              echoSinceLastReview:
                data.echoSinceLastReview === "YES"
                  ? true
                  : data.echoSinceLastReview === "NOT_AVAILABLE"
                  ? null
                  : false,
              echoResult: data.echoResult || null,
              lastEchoDate: data.lastEchoDate
                ? Number(data.lastEchoDate)
                : null,
            },
            laboratoryInvestigations: {
              haemoglobin: toNumberOrUndefined(data.haemoglobin),
              totalCholesterol: toNumberOrUndefined(data.totalCholesterol),
              hdlCholesterol: toNumberOrUndefined(data.hdlCholesterol),
              nonHdlCholesterol: toNumberOrUndefined(data.nonHdlCholesterol),
              ldlCholesterol: data.ldlCholesterol,
              triglycerides: toNumberOrUndefined(data.triglycerides),
              hba1c: toNumberOrUndefined(data.hba1c),
              serumCreatinine: toNumberOrUndefined(data.serumCreatinine),
              egfr: data.egfr,
              // FastingGlucose + ACR — pending BE (see KIN ticket for PP labs);
              // PpLaboratoryInvestigationsDto must add them (String). Ignored until then.
              fastingGlucose: data.fastingGlucose || null,
              acr: data.acr === "NA" ? "NA" : Number(data.acr) || null,
              // NT-proBNP — hidden until lab capacity is increased (KIN-1050).
              // PpLaboratoryInvestigationsDto must add `ntProBNP`; ignored until then.
              ntProBNP: data.ntProBNP === "NA" ? "NA" : Number(data.ntProBNP) || null,
              // TFT/LFT registration-only — null (not collected) at annual review.
              tftConducted:
                data.tftConducted === "YES"
                  ? true
                  : data.tftConducted === "NO"
                    ? false
                    : null,
              lftConducted:
                data.lftConducted === "YES"
                  ? true
                  : data.lftConducted === "NO"
                    ? false
                    : null,
            },
            patientEducation: {
              educationProvided: data.educationProvided || null,
              referredToEducation: data.referredToEducation || null,
              referredToDiabetesPrevention: data.referredToDiabetesPrevention || null,
              referralDetails: data.referralDetails || null,
              agreedWrittenCarePlan: data.agreedWrittenCarePlan || null,
            },
            carePlan: data.carePlan,
          },
          confirmedOutcome: {
            diagnosedWithChronicDisease:
              data.diagnosedWithChronicDisease === "YES" ? true : false,
            cdmDiagnoses: [
              data.diabetesType2 ? "DIABETES_TYPE_2" : null,
              data.asthma ? "ASTHMA" : null,
              data.copd ? "COPD" : null,
              data.heartFailure ? "HEART_FAILURE" : null,
              data.atrialFibrillation ? "ATRIAL_FIBRILLATION" : null,
              data.ischaemicHeartDisease ? "ISCHAEMIC_HEART_DISEASE" : null,
              data.stroke ? "STROKE" : null,
              data.tia ? "TIA" : null,
            ].filter((item) => item !== null),
          },
        }
      );

      return response.data;
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
      queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
      syncBaseline(variables);
    },
  });
};
